/** Direct native radio choices retain DOM/focus. Arrow/Home/End keys select enabled choices;
 * optional translated shortLabel changes visible copy only, preserving the full radio name/title. */
export function createDirectChoices(
  ctx,
  {
    label,
    name,
    value,
    options,
    images = false,
    variant = 'profile',
    searchable = false,
    onSelect = () => {},
  },
) {
  const root = ctx.e('section', `direct-choice-field ${variant}-choice-field`);
  const node = ctx.e(
    'div',
    variant === 'profile'
      ? images
        ? 'profile-photo-choices'
        : 'profile-audience-choices'
      : `direct-choices${images ? ' direct-image-choices' : ''}`,
  );
  if (variant !== 'profile') root.append(ctx.e('span', 'direct-choice-title', label));
  root.append(node);
  node.setAttribute('role', 'radiogroup');
  node.setAttribute('aria-label', label);
  const entries = [];
  let selected = value;
  const available = () => entries.filter((entry) => !entry.input.disabled && !entry.label.hidden);
  // A selection may synchronously repaint its consumer. Focus the replacement
  // by its unique radio-group name/value rather than a detached input node.
  function focusChoice(next) {
    const original = entries.find((entry) => entry.value === next)?.input;
    const target =
      original?.isConnected !== false
        ? original
        : [...(globalThis.document?.querySelectorAll?.('input[type="radio"]') ?? [])].find(
            (input) =>
              input.name === name &&
              input.value === next &&
              !input.disabled &&
              input.isConnected !== false,
          );
    target?.focus({ preventScroll: true });
  }
  function choose(next, notify = true) {
    const entry = entries.find((item) => item.value === next);
    if (!entry || entry.input.disabled) return;
    selected = next;
    for (const item of entries) {
      item.input.checked = item === entry;
      item.input.tabIndex = item === entry ? 0 : -1;
      item.label.classList.toggle('selected', item === entry);
    }
    if (notify) {
      const result = onSelect(next);
      if (entry.input.isConnected === false) focusChoice(next);
      return result;
    }
  }
  for (const option of options) {
    const choice = ctx.e('label', 'profile-choice');
    const copy = option.literal ? option.label : ctx.uiText(option.label);
    choice.title = copy;
    const input = ctx.field('input', {
      type: 'radio',
      name,
      value: option.value,
      disabled: option.disabled === true,
      ariaLabel: copy,
    });
    input.className = 'profile-choice-input';
    input.addEventListener('change', () => {
      if (input.checked) return choose(option.value);
    });
    choice.append(input);
    if (option.image)
      choice.append(
        ctx.field('img', { src: ctx.photoSource(option.image, 160), alt: '', loading: 'lazy' }),
      );
    else choice.append(ctx.icon(option.icon ?? 'image'));
    choice.append(ctx.e('span', 'profile-choice-label', option.shortLabel ?? copy));
    node.append(choice);
    entries.push({ input, label: choice, value: option.value });
  }
  choose(
    entries.some((entry) => entry.value === value && !entry.input.disabled)
      ? value
      : entries.find((entry) => !entry.input.disabled)?.value,
    false,
  );
  node.addEventListener('keydown', (event) => {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key))
      return;
    event.preventDefault();
    const enabled = available();
    if (!enabled.length) return;
    const index = enabled.findIndex((entry) => entry.value === selected);
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? enabled.length - 1
          : (index + (['ArrowLeft', 'ArrowUp'].includes(event.key) ? -1 : 1) + enabled.length) %
            enabled.length;
    choose(enabled[next].value);
    focusChoice(enabled[next].value);
  });
  if (searchable && options.length > 8) {
    const search = ctx.field('input', {
      type: 'search',
      ariaLabel: label,
      placeholder: ctx.uiText('Search'),
    });
    search.addEventListener('input', () => {
      const query = search.value.trim().toLocaleLowerCase();
      for (const entry of entries)
        entry.label.hidden = !entry.label.title.toLocaleLowerCase().includes(query);
      const visible = available();
      const tab = visible.find((entry) => entry.value === selected) ?? visible[0];
      for (const entry of entries) entry.input.tabIndex = entry === tab ? 0 : -1;
    });
    root.prepend(search);
  }
  return {
    node: variant === 'profile' ? node : root,
    get value() {
      return selected;
    },
    setValue: (next) => choose(next, false),
    enable(next, value) {
      const entry = entries.find((item) => item.value === next);
      if (entry) {
        entry.input.disabled = !value;
        if (!value && selected === next) choose(available()[0]?.value, false);
      }
    },
  };
}
